<template>
  <div class="flex flex-col">
    <div class="bg-gray-800 rounded-lg overflow-hidden mb-4 relative shadow-lg">
      <img
        v-if="currentImage"
        :src="getImageUrl(currentImage)"
        :alt="product.name"
        class="w-full h-96 object-contain cursor-pointer"
        @click="$emit('open-lightbox')"
      />
      <div
        v-else
        class="w-full h-96 flex items-center justify-center bg-gray-800 text-gray-500"
        style="width: 520px; height: 384px"
      >
        <span>Nema dostupnih slika</span>
      </div>

      <!-- Fullscreen icon -->
      <div
        v-if="currentImage"
        @click="$emit('open-lightbox')"
        class="absolute top-3 right-3 bg-gray-900 bg-opacity-70 p-2 rounded-md cursor-pointer hover:bg-opacity-90 transition-opacity text-white hover:bg-blue-900"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
          <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
          <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
          <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
        </svg>
      </div>

      <!-- Navigation arrows -->
      <div
        v-if="product?.pictures?.length > 1"
        class="absolute inset-x-0 top-1/2 transform -translate-y-1/2 flex justify-between px-4"
      >
        <button
          @click.stop="navigateToPreviousImage"
          class="bg-gray-900 bg-opacity-70 hover:bg-opacity-90 rounded-full p-2 text-white focus:outline-none transition-opacity hover:text-blue-400"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M15 18l-6-6 6-6"></path>
          </svg>
        </button>

        <button
          @click.stop="navigateToNextImage"
          class="bg-gray-900 bg-opacity-70 hover:bg-opacity-90 rounded-full p-2 text-white focus:outline-none transition-opacity hover:text-blue-400"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M9 18l6-6-6-6"></path>
          </svg>
        </button>
      </div>
    </div>

    <!-- Thumbnails -->
    <div
      v-if="product.pictures && product.pictures.length > 1"
      class="thumbnails-container"
    >
      <div
        v-for="picture in product.pictures"
        :key="picture.id"
        @click="updateCurrentImage(picture.path)"
        class="thumbnail-item"
        :class="{
          'border-2 border-green-500': currentImage === picture.path,
        }"
      >
        <img
          :src="getImageUrl(picture.path)"
          :alt="product.name"
          class="w-full h-full object-cover"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, computed, PropType } from 'vue';
import { Product } from '@/composables/useProductDetail';
import { getImageUrl as getImageUrlUtil } from '@/utils/imageUtils'; // DODATO

export default defineComponent({
  name: 'ProductGallery',
  props: {
    product: {
      type: Object as PropType<Product>,
      required: true,
    },
    currentImage: {
      type: String,
      default: null,
    },
    // apiUrl prop se više ne koristi, ali ostaviti za kompatibilnost
    apiUrl: {
      type: String,
      required: false,
      default: '',
    },
  },
  emits: ['update:current-image', 'open-lightbox'],
  setup(props, { emit }) {
    const currentImageIndex = computed(() => {
      if (!props.product || !props.currentImage) return 0;

      const index = props.product.pictures.findIndex(
        (pic) => pic.path === props.currentImage
      );
      return index >= 0 ? index : 0;
    });

    // ZAMENJENA FUNKCIJA - koristi novi imageUtils
    const getImageUrl = (imagePath: string): string => {
      return getImageUrlUtil(imagePath);
    };

    const updateCurrentImage = (path: string) => {
      emit('update:current-image', path);
    };

    const navigateToPreviousImage = () => {
      if (!props.product || !props.product.pictures.length) return;

      const currentIndex = currentImageIndex.value;
      const newIndex =
        currentIndex <= 0
          ? props.product.pictures.length - 1 // Go to last image if at first
          : currentIndex - 1; // Otherwise go to previous

      emit('update:current-image', props.product.pictures[newIndex].path);
    };

    const navigateToNextImage = () => {
      if (!props.product || !props.product.pictures.length) return;

      const currentIndex = currentImageIndex.value;
      const newIndex =
        currentIndex >= props.product.pictures.length - 1
          ? 0 // Go back to first image if at last
          : currentIndex + 1; // Otherwise go to next

      emit('update:current-image', props.product.pictures[newIndex].path);
    };

    return {
      currentImageIndex,
      getImageUrl,
      updateCurrentImage,
      navigateToPreviousImage,
      navigateToNextImage,
    };
  },
});
</script>

<style scoped>
/* Thumbnails container styling - Desktop (kao pre) */
.thumbnails-container {
  display: flex;
  gap: 8px;
  padding-bottom: 8px;
  flex-wrap: wrap;
  max-width: 100%;
}

/* Thumbnail item styling - Desktop */
.thumbnail-item {
  width: 80px;
  height: 80px;
  background-color: #374151;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  transition: opacity 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.thumbnail-item:hover {
  opacity: 0.8;
}

/* Mobile responsive styles - Grid layout umesto klizača */
@media (max-width: 768px) {
  .thumbnails-container {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
    gap: 6px;
    padding-bottom: 6px;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    /* Uklanjamo overflow-x da nema klizača */
    overflow-x: visible;
  }

  .thumbnail-item {
    width: 60px;
    height: 60px;
    border-radius: 4px;
    /* Uklanjamo min-width i flex-shrink jer koristimo grid */
    justify-self: center;
  }

  /* Main image container adjustments for mobile */
  .bg-gray-800 {
    margin-left: 0;
    margin-right: 0;
  }
}

@media (max-width: 480px) {
  .thumbnails-container {
    grid-template-columns: repeat(auto-fit, minmax(50px, 1fr));
    gap: 4px;
    padding-bottom: 4px;
    padding-left: 2px;
    padding-right: 2px;
  }

  .thumbnail-item {
    width: 50px;
    height: 50px;
    border-radius: 4px;
  }

  /* Further adjustments for very small screens */
  .bg-gray-800 {
    border-radius: 8px;
  }

  /* Adjust main image height for small screens */
  .h-96 {
    height: 280px;
  }
}

/* Ensure no horizontal overflow */
@media (max-width: 640px) {
  * {
    box-sizing: border-box;
  }

  /* Prevent any element from causing horizontal scroll */
  .flex {
    max-width: 100%;
    overflow: hidden;
  }

  /* Ensure flex container doesn't overflow */
  .flex.flex-col {
    width: 100%;
    max-width: 100%;
  }
}

/* Landscape orientation on mobile */
@media (max-width: 768px) and (orientation: landscape) {
  .h-96 {
    height: 240px;
  }

  .thumbnail-item {
    width: 55px;
    height: 55px;
  }

  .thumbnails-container {
    grid-template-columns: repeat(auto-fit, minmax(55px, 1fr));
  }
}
</style>
